// ================================================================
// api.ts — Typed fetch wrapper for AI Governance Platform API
//
// All calls attach the MSAL Bearer token.
// 401 → triggers re-login.
// ================================================================

import { getAccessToken, login } from './auth';
import { getApiKeySession, clearApiKeySession } from './apikey-session';
import {
  isDemoMode, getDemoEvents, DEMO_TENANT, DEMO_CONVERSATION_LIST,
  DEMO_CONVERSATIONS, DEMO_API_KEYS,
} from './demo';

export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** Demo 模式：模擬真實網路延遲讓 loading 動畫正常顯示 */
function demoDelay<T>(ms: number, value: T): Promise<T> {
  return new Promise(resolve => setTimeout(() => resolve(value), ms));
}

/** 取得 auth token：API Key session 優先，其次 MSAL */
async function getAuthToken(): Promise<string | null> {
  const akSession = getApiKeySession();
  if (akSession) return akSession.token;
  return getAccessToken();
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let token = await getAuthToken();
  if (!token) {
    await login();
    token = await getAuthToken();
  }
  if (!token) throw new Error('Unable to acquire access token');

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    // 服務帳號（API Key）session 過期 → 清除並回登入頁，不要觸發 MSAL 整頁導向（避免迴圈）
    if (getApiKeySession()) {
      clearApiKeySession();
      window.location.reload();
      throw new Error('Session expired. Please sign in again.');
    }
    // MSAL session 失效 → 以 redirect flow 重新登入
    await login();
    throw new Error('Session expired. Please try again.');
  }
  if (!res.ok) {
    const err = await res.json<{ error: string; code: string }>().catch(() => ({ error: res.statusText, code: 'UNKNOWN' }));
    throw new Error(`API error [${err.code}]: ${err.error}`);
  }
  return res.json<T>();
}

// ── Events ───────────────────────────────────────────────────
export interface EventsQuery {
  user?: string;
  service?: string;
  type?: 'interaction' | 'visit' | 'dlp';
  dlp?: '1';
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

export interface EventRow {
  id: string;
  tenant_id: string;
  event_time: string;
  user_upn: string;
  user_name: string | null;
  department: string | null;
  ai_service: string;
  app_class: string | null;
  event_type: string;
  source_device: string | null;
  has_content: boolean;
  session_id: string | null;
  dlp_hit: boolean;
}

export async function fetchEvents(query: EventsQuery = {}) {
  if (isDemoMode()) {
    let events = getDemoEvents();
    // 套用篩選
    if (query.user)    events = events.filter(e => e.user_upn.includes(query.user!));
    if (query.service) events = events.filter(e => e.ai_service === query.service);
    if (query.dlp)     events = events.filter(e => e.dlp_hit);
    if (query.from)    events = events.filter(e => e.event_time >= query.from!);
    if (query.to)      events = events.filter(e => e.event_time <= query.to! + 'T23:59:59Z');
    const limit = query.limit ?? 50;
    const sliced = events.slice(0, limit);
    return demoDelay(400, { data: sliced, nextCursor: events.length > limit ? 'demo-cursor' : null });
  }
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => { if (v != null) params.set(k, String(v)); });
  // /api/activity（非 /api/events）以避開廣告/隱私擋擴充對 "events" 關鍵字的封鎖
  return apiFetch<{ data: EventRow[]; nextCursor: string | null }>(`/api/activity?${params}`);
}

// ── Conversations ─────────────────────────────────────────────
export interface ConversationIndex {
  session_id: string;
  tenant_id: string;
  user_upn: string;
  user_name: string | null;
  department: string | null;
  ai_service: string;
  start_time: string;
  dlp_hit: boolean;
  msg_count: number;
}

export interface ConversationMessage {
  role: 'user' | 'ai';
  sender?: string;                    // 發話者顯示名稱（人員姓名 / AI 服務或 agent 名）
  timestamp: string;
  body: string;
  dlp_alert?: string;
  contexts?: string[];
}

export interface ConversationFull extends ConversationIndex {
  messages: ConversationMessage[];
}

export async function fetchConversations(limit = 50, cursor?: string) {
  if (isDemoMode()) {
    return demoDelay(300, { data: DEMO_CONVERSATION_LIST, nextCursor: null });
  }
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return apiFetch<{ data: ConversationIndex[]; nextCursor: string | null }>(`/api/conversations?${params}`);
}

export async function fetchConversationById(sessionId: string) {
  if (isDemoMode()) {
    const conv = DEMO_CONVERSATIONS.find(c => c.session_id === sessionId);
    if (!conv) throw new Error('Demo conversation not found');
    return demoDelay(200, { data: conv });
  }
  return apiFetch<{ data: ConversationFull }>(`/api/conversations/${sessionId}`);
}

// ── Tenant ────────────────────────────────────────────────────
export interface TenantInfo {
  tenant_id: string;
  name: string;
  domain: string | null;
  status: string;
  consent_state: string;
  dataverse_url?: string | null;
}

export async function fetchMyTenant() {
  if (isDemoMode()) return demoDelay(200, { data: DEMO_TENANT });
  return apiFetch<{ data: TenantInfo }>('/api/me/tenant');
}

/** 同步 agent 監控狀態到後端（後端才會擷取該 agent 對話）*/
export async function setAgentMonitored(agentId: string, agentName: string, monitored: boolean) {
  return apiFetch<{ ok: boolean }>('/api/agents/monitor', {
    method: 'POST',
    body: JSON.stringify({ agentId, agentName, monitored }),
  });
}

/** 掃描 Copilot Studio Agent（POST，需授權；後端從 session 取 tenant）*/
export async function scanAgents() {
  return apiFetch<{ agents: any[]; count: number; scannedAt: string }>(
    '/api/agents/scan', { method: 'POST' }
  );
}

/** 設定租戶的 Dataverse 環境 Org URL（TenantAdmin）；傳空字串可清除 */
export async function setDataverseUrl(url: string) {
  if (isDemoMode()) return demoDelay(300, { ok: true, dataverseUrl: url });
  return apiFetch<{ ok: boolean; dataverseUrl: string | null }>(
    '/api/settings/dataverse-url',
    { method: 'POST', body: JSON.stringify({ url }) }
  );
}

// ── API Keys ──────────────────────────────────────────────────
export interface ApiKeyInfo {
  key_id:       string;
  key_prefix:   string;
  label:        string;
  roles:        string;   // JSON string
  created_at:   number;
  last_used_at: number | null;
  expires_at:   number | null;
}

export async function fetchApiKeys() {
  if (isDemoMode()) return demoDelay(300, { data: DEMO_API_KEYS });
  return apiFetch<{ data: ApiKeyInfo[] }>('/api/apikeys');
}

export async function createApiKey(label: string, expiresInDays?: number) {
  if (isDemoMode()) {
    return demoDelay(600, {
      keyId: 'key-demo-new', apiKey: 'aig_demo00000000000000000000000000',
      keyPrefix: 'aig_demo0000', label, roles: ['Viewer'],
      expiresAt: expiresInDays ? Math.floor(Date.now() / 1000) + expiresInDays * 86400 : null,
    });
  }
  return apiFetch<{ keyId: string; apiKey: string; keyPrefix: string; label: string; roles: string[] }>(
    '/api/apikeys',
    { method: 'POST', body: JSON.stringify({ label, expiresInDays }) }
  );
}

export async function revokeApiKey(keyId: string) {
  if (isDemoMode()) return demoDelay(400, { ok: true });
  return apiFetch<{ ok: boolean }>(`/api/apikeys/${keyId}`, { method: 'DELETE' });
}
